import {
  Box,
  Chip,
  Divider,
  Icon,
  SxProps,
  Tooltip,
  Typography
} from "@outschool/backpack";
import { OSSubscriptions } from "@outschool/business-rules";
import { faQuestionCircle, faTag } from "@outschool/icons";
import { renderCurrencyString, useTranslation } from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import React from "react";
import { Trans } from "react-i18next";

import * as Env from "../../../shared/Env";

function PlanBenefitListItem({ children }: React.PropsWithChildren<{}>) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        marginBottom: 4,
        alignItems: "flex-start"
      }}
    >
      <Box
        sx={{
          marginTop: "1px",
          paddingRight: 4,
          color: "success.main",
          fontSize: 14,
          width: 16
        }}
      >
        •
      </Box>
      <Box sx={{ display: "flex", flex: 1, alignItems: "center" }}>
        {children}
      </Box>
    </Box>
  );
}

export const PlanBenefitsList = ({
  plan
}: {
  plan: {
    uid: string;
    classPerWeekAvg: number;
    numRollOverCredits: number;
    numFreeCredits: number;
  };
  isSavingToolTipOpen?: boolean;
  setIsSavingToolTipOpen?: (x: boolean) => void;
  tooltipRef?: React.MutableRefObject<null>;
}) => {
  const numRollOverCredits = plan.numRollOverCredits;
  const numFreeCredits = plan.numFreeCredits;
  const creditValue = renderCurrencyString({
    priceInCents: OSSubscriptions.creditsToPriceCents(numFreeCredits),
    showCurrencyCode: false
  });
  const classPerWeekAvg = plan.classPerWeekAvg;
  const { t } = useTranslation(
    "client\\components\\Membership\\MembershipOptionCard"
  );

  return (
    <>
      {numFreeCredits > 0 && (
        <PlanBenefitListItem>
          <Typography variant={"body2"}>
            <Trans t={t}>
              <b>{{ numFreeCredits }} bonus credits</b>/mo ({{ creditValue }}{" "}
              value) included
            </Trans>
          </Typography>
        </PlanBenefitListItem>
      )}
      <PlanBenefitListItem>
        <Typography variant={"body2"}>
          {plan.classPerWeekAvg === 1 ? (
            <Trans t={t}>
              Good for an average of <b>{{ classPerWeekAvg }}</b> group class
              per week; add credits anytime
            </Trans>
          ) : (
            <Trans t={t}>
              Good for an average of <b>{{ classPerWeekAvg }}</b> group classes
              per week; add credits anytime
            </Trans>
          )}
        </Typography>
      </PlanBenefitListItem>
      <PlanBenefitListItem>
        <Typography variant={"body2"}>
          <Trans t={t}>
            Roll over <b>up to {{ numRollOverCredits }}</b> credits
          </Trans>
        </Typography>
      </PlanBenefitListItem>
      <PlanBenefitListItem>
        <Typography variant={"body2"}>{t("Concierge support")}</Typography>
        <Tooltip
          sx={{
            width: "fit-content"
          }}
          title={
            <Typography>
              {t(
                "Get exclusive access to our Concierge — a pilot program designed to \
                enhance your Membership experience. Enjoy priority support, personalized \
                class recommendations, and enrollment guidance."
              )}
            </Typography>
          }
          placement="top"
          describeChild
        >
          <Icon
            size="inherit"
            icon={faQuestionCircle}
            sx={{
              marginLeft: 4,
              fontSize: 14
            }}
          />
        </Tooltip>
      </PlanBenefitListItem>
    </>
  );
};

export function MembershipOptionCard({
  plan,
  isRecommended,
  onSelect,
  isActive = false,
  sx,
  priceLineContainerSx,
  showShadows = true,
  couponPercentDiscount
}: {
  plan: {
    uid: string;
    numCredits: number;
    amountCents: number;
    classPerWeekAvg: number;
    numRollOverCredits: number;
    numFreeCredits: number;
  };
  isRecommended?: boolean;
  onSelect: (e: React.MouseEvent<HTMLElement>) => void;
  isActive?: boolean;
  sx?: SxProps;
  priceLineContainerSx?: SxProps;
  showShadows?: boolean;
  couponPercentDiscount?: number;
}) {
  const { t } = useTranslation(
    "client\\components\\Membership\\MembershipOptionCard"
  );
  const { isEsaSession } = useSession();
  const [isSavingToolTipOpen, setIsSavingToolTipOpen] = React.useState(false);
  const tooltipRef = React.useRef(null);

  // Calculate the total percent discount, defined by marketing as:
  // (the $ value of the plan's "free credits" + the $ savings from coupon % discount) / ($ price of plan + $ value of free credits)
  const freeCreditsValueInCents =
    plan.numFreeCredits * OSSubscriptions.CREDIT_TO_PRICE_CENTS_CONVERSION_RATE;
  const savingsFromCouponInCents =
    plan.amountCents * ((couponPercentDiscount || 0) / 100);
  const totalPercentDiscount =
    ((savingsFromCouponInCents + freeCreditsValueInCents) /
      (plan.amountCents + freeCreditsValueInCents)) *
    100;

  const handleClickOutside = (event: MouseEvent) => {
    if (
      tooltipRef.current &&
      // @ts-ignore
      !tooltipRef.current.contains(event.target as Node)
    ) {
      setIsSavingToolTipOpen(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <Box
      onClick={onSelect}
      sx={{
        display: "flex",
        flexDirection: "column",
        paddingY: 20,
        paddingX: 16,
        backgroundColor: "white",
        minWidth: 256,
        minHeight: 387.5,
        width: "100%",
        borderRadius: 16,
        boxShadow:
          isActive && showShadows
            ? "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 16px 32px 0px rgba(0, 0, 0, 0.10)"
            : "unset",
        border: isActive
          ? "1px solid var(--border-border-peacock-darker, #4B01D4)"
          : "1px solid var(--border-border-neutral-lighter, #E0E0E0)",
        "&:hover": {
          cursor: "pointer",
          boxShadow: showShadows
            ? "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 16px 32px 0px rgba(0, 0, 0, 0.10)"
            : "unset"
        },
        ...sx
      }}
    >
      <Box
        flex
        sx={{
          marginBottom: 9.5,
          justifyContent: !!couponPercentDiscount ? "space-between" : "start"
        }}
      >
        <Typography as="span" variant="subtitle1">
          <b>{plan.numCredits}</b> {t`credits`}
        </Typography>
        {isRecommended && !couponPercentDiscount && (
          <Chip
            sx={{ marginLeft: 4 }}
            size="small"
            color="primary"
            label={t`Recommended`}
          />
        )}
        {!!couponPercentDiscount && (
          <Chip
            size="small"
            color="primary"
            icon={<Icon icon={faTag} />}
            label={t("{{totalPercentDiscount}}% off", {
              totalPercentDiscount: Math.floor(totalPercentDiscount).toString()
            })}
          />
        )}
      </Box>
      <Box sx={priceLineContainerSx}>
        <Box>
          <Typography as="span" variant="display2" sx={{ marginBottom: 24 }}>
            {renderCurrencyString({
              priceInCents: plan.amountCents,
              showCurrencyCode: false
            })}
          </Typography>{" "}
          <Typography as="span" variant="subtitle1">
            {t`USD / month`}
          </Typography>
        </Box>
        <Tooltip
          title={
            !!isEsaSession ? (
              <Trans t={t}>
                Memberships aren’t available with state ESA, microgrant, or
                scholarship funds. For additional information, contact
                <a href="mailto:support@outschool.com">support@outschool.com</a>
                .
              </Trans>
            ) : null
          }
        >
          <TrackedButton
            trackingName="join_memberships_choose_a_plan_select_button"
            trackingSharedProperties={{ plan: plan.uid }}
            variant={isActive ? "contained" : "outlined"}
            sx={{ marginTop: 24, width: "fit-content" }}
            onClick={onSelect}
            disabled={isEsaSession || Env.IS_READ_ONLY_MODE}
          >
            {t("Select")}
          </TrackedButton>
        </Tooltip>
      </Box>
      <Divider sx={{ marginY: 24 }} />
      <PlanBenefitsList
        plan={plan}
        isSavingToolTipOpen={isSavingToolTipOpen}
        setIsSavingToolTipOpen={setIsSavingToolTipOpen}
        tooltipRef={tooltipRef}
      />
    </Box>
  );
}
